import { useEffect, useState } from 'react';
import { useAIAnalysis } from '@/hooks/useAIAnalysis';

interface AIAnalysisModalProps {
  isOpen: boolean;
  onComplete: (data: unknown) => void;
  campaignId: string | null;
  cvPath?: string;
  jobDescriptionPath?: string;
  jobDescriptionText?: string;
}

export const AIAnalysisModal = ({
  isOpen,
  onComplete,
  campaignId,
  cvPath,
  jobDescriptionPath,
  jobDescriptionText
}: AIAnalysisModalProps) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('Analyse en cours...');
  const [hasCompleted, setHasCompleted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const { isAnalyzing: _isAnalyzing, analysisCompleted: _analysisCompleted, analysisError, triggerAnalysis } = useAIAnalysis({
    campaignId,
    cvPath,
    jobDescriptionPath,
    jobDescriptionText,
    onDataExtracted: (data) => {
      setProgress(100);
      setStatusText('Analyse terminée !');
      setHasCompleted(true);
      // Attendre un peu avant de fermer la modal
      setTimeout(() => {
        onComplete(data);
      }, 1500);
    },
    skipAutoAnalysis: true, // On veut contrôler manuellement le déclenchement
  });

  // Réinitialiser quand la modal s'ouvre/ferme
  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setStatusText('Analyse en cours...');
      setHasCompleted(false);
      setHasStarted(false);
      return;
    }
  }, [isOpen]);

  // Démarrer l'analyse une seule fois quand la modal s'ouvre
  useEffect(() => {
    if (!isOpen || hasStarted || hasCompleted) return;

    if (campaignId && (cvPath || jobDescriptionPath || jobDescriptionText)) {
      setHasStarted(true);
      triggerAnalysis();
    }
  }, [isOpen, campaignId, cvPath, jobDescriptionPath, jobDescriptionText, hasStarted, hasCompleted, triggerAnalysis]);

  // Animation de progression monotone (ne peut qu'avancer)
  useEffect(() => {
    if (!isOpen || !hasStarted || hasCompleted) return;

    const steps = [
      { progress: 15, text: 'Initialisation de l\'analyse...', duration: 500 },
      { progress: 30, text: 'Extraction des informations du CV...', duration: 1500 },
      { progress: 50, text: 'Analyse de la description du poste...', duration: 1500 },
      { progress: 70, text: 'Génération des critères d\'évaluation...', duration: 1000 },
      { progress: 85, text: 'Recommandation des référents...', duration: 1000 },
      { progress: 95, text: 'Finalisation de l\'analyse...', duration: 500 }
    ];

    let currentStepIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const progressToNextStep = () => {
      if (currentStepIndex < steps.length && !hasCompleted) {
        const step = steps[currentStepIndex];

        // Ne progresser que si on n'a pas déjà atteint ce niveau
        setProgress(prev => Math.max(prev, step.progress));
        setStatusText(step.text);

        currentStepIndex++;
        timeoutId = setTimeout(progressToNextStep, step.duration);
      }
    };

    // Démarrer la progression
    timeoutId = setTimeout(progressToNextStep, 200);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, hasStarted, hasCompleted]);

  // Gérer les erreurs d'analyse
  useEffect(() => {
    if (analysisError && isOpen && !hasCompleted) {
      setStatusText('Erreur lors de l\'analyse');
      // Ne pas réinitialiser le progress à 0, garder la progression actuelle
      setTimeout(() => {
        onComplete(null);
      }, 2000);
    }
  }, [analysisError, isOpen, hasCompleted, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50">
      <div className="px-6 py-8 bg-white rounded-3xl flex flex-col justify-center items-center gap-6 w-96 mx-4">
        <div className="flex flex-col justify-center items-center gap-1">
          <div className="text-gray-800 text-xl font-semibold font-bricolage">Analyse en cours</div>
          <div className="w-96 text-center text-neutral-400 text-sm font-normal font-bricolage max-w-full">
            Notre IA extrait les expériences du candidat, identifie les référents pertinents et génère vos critères d'évaluation personnalisés.
          </div>
        </div>

        <div className="w-full h-3.5 relative bg-teal-400/10 rounded-full flex justify-start items-start overflow-hidden">
          <div
            className="h-full bg-teal-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-gray-800 text-[10px] font-normal font-bricolage">
            {progress}%
          </div>
        </div>

        <div className="text-teal-400 text-sm font-medium font-bricolage">
          {statusText}
        </div>
      </div>
    </div>
  );
};